import { POSITION_DESCRIPTION_MD } from '../lib/positionDescriptionTemplate';

// Public position-description page on bandn. Reached via the email
// links sent after signing (and from the AUHCA candidate portal). Same
// content for every ACILA Personal Care Assistant placement — generic
// role description, no employer-specific dynamic fields.
//
// Routing: registered as a standalone route in App.js so the bandn
// marketing header/footer is hidden — keeps the page focused on the
// document itself, no nav distractions.
//
// Source of truth: HCA-POSITION-DESCRIPTION.md in the AUHCA repo,
// sync'd via scripts/auhcaSyncContractTemplate.js.

const ORANGE = '#EE6C5A';
const WARM_BG = '#FBF6F0';
const CARD_BG = '#FFFFFF';
const INK_900 = '#1F1D1B';
const INK_700 = '#3D3935';
const INK_500 = '#6E6A66';
const INK_BORDER = '#E2DCD4';

const PositionDescription = () => {
  return (
    <div style={{
      minHeight: '100vh',
      background: WARM_BG,
      color: INK_900,
      fontFamily: '"Inter", system-ui, sans-serif',
    }}>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '40px 20px 80px' }}>
        <header style={{ marginBottom: 22 }}>
          <p style={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', color: ORANGE,
            textTransform: 'uppercase', marginBottom: 6,
          }}>
            Position Description · Schedule A
          </p>
          <p style={{ color: INK_500, fontSize: 13, marginTop: 4 }}>
            Standard role description for Personal Care Assistant (ANZSCO 423313)
            engagements under the Aged Care Industry Labour Agreement.
          </p>
        </header>

        <article style={{
          background: CARD_BG,
          border: `1px solid ${INK_BORDER}`,
          borderRadius: 14,
          padding: 'clamp(20px, 4vw, 36px)',
          fontSize: 14,
          lineHeight: 1.65,
          color: INK_700,
        }}>
          <PdMarkdown md={POSITION_DESCRIPTION_MD} />
        </article>

        <p style={{
          marginTop: 18, fontSize: 12, color: INK_500, textAlign: 'center',
        }}>
          This Position Description is incorporated by reference into the Employee's
          Employment Contract as Schedule A.
        </p>
      </div>
    </div>
  );
};

// Minimal markdown → JSX renderer. Same primitive set the signing
// page's ContractMarkdown supports (H1/H2/H3, bullets, paragraphs,
// bold + italic). Local to this page so the two renderers can evolve
// independently if needed.
const PdMarkdown = ({ md }) => {
  const elements = [];
  const lines = md.split('\n');
  let bulletGroup = null;

  const flushBullets = () => {
    if (bulletGroup && bulletGroup.length) {
      elements.push(
        <ul key={`ul-${elements.length}`} style={{ margin: '8px 0 14px 22px', padding: 0 }}>
          {bulletGroup}
        </ul>
      );
    }
    bulletGroup = null;
  };

  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (line === '') { flushBullets(); return; }
    if (line === '---') {
      flushBullets();
      elements.push(<hr key={i} style={{ border: 0, borderTop: `1px solid ${INK_BORDER}`, margin: '20px 0' }} />);
      return;
    }

    if (line.startsWith('# ')) {
      flushBullets();
      elements.push(
        <h1 key={i} style={{ fontSize: 24, fontWeight: 700, color: INK_900, margin: '0 0 12px', letterSpacing: '-0.01em' }}>
          {parseInline(line.slice(2))}
        </h1>
      );
      return;
    }
    if (line.startsWith('## ')) {
      flushBullets();
      elements.push(
        <h2 key={i} style={{ fontSize: 16, fontWeight: 700, color: ORANGE, margin: '22px 0 8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {parseInline(line.slice(3))}
        </h2>
      );
      return;
    }
    if (line.startsWith('### ')) {
      flushBullets();
      elements.push(
        <h3 key={i} style={{ fontSize: 14, fontWeight: 600, color: INK_900, margin: '16px 0 6px' }}>
          {parseInline(line.slice(4))}
        </h3>
      );
      return;
    }
    if (line.startsWith('- ')) {
      if (!bulletGroup) bulletGroup = [];
      bulletGroup.push(
        <li key={i} style={{ marginBottom: 6 }}>{parseInline(line.slice(2))}</li>
      );
      return;
    }

    flushBullets();
    elements.push(
      <p key={i} style={{ margin: '6px 0 12px' }}>{parseInline(line)}</p>
    );
  });
  flushBullets();
  return <>{elements}</>;
};

function parseInline(text) {
  const out = [];
  let i = 0;
  let buf = '';
  let bold = false;
  let italic = false;
  const flush = () => {
    if (!buf) return;
    let node = buf;
    if (bold && italic) node = <strong key={out.length}><em>{node}</em></strong>;
    else if (bold) node = <strong key={out.length}>{node}</strong>;
    else if (italic) node = <em key={out.length}>{node}</em>;
    out.push(node);
    buf = '';
  };
  while (i < text.length) {
    if (text[i] === '*' && text[i + 1] === '*') { flush(); bold = !bold; i += 2; continue; }
    if (text[i] === '*') { flush(); italic = !italic; i += 1; continue; }
    buf += text[i++];
  }
  flush();
  return out.length ? out : '';
}

export default PositionDescription;
